import React, { useState, useEffect } from 'react';
import { Task, User as UserType } from '../types';
import { X, Calendar, User, AlignLeft, CheckCircle2, ChevronDown, Flag, Briefcase } from 'lucide-react';

interface TaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Partial<Task>) => void;
  editingTask: Task | null;
  users: UserType[];
  tasks: Task[];
}

export const TaskFormModal: React.FC<TaskFormModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingTask, 
  users 
}) => {
  const [formData, setFormData] = useState<Partial<Task>>({
    title: '',
    description: '',
    priority: 'Medium',
    dueDate: new Date().toISOString().split('T')[0],
    assigneeId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingTask) {
      setFormData(editingTask);
    } else {
      setFormData({
        title: '',
        description: '',
        priority: 'Medium',
        dueDate: new Date().toISOString().split('T')[0],
        assigneeId: '',
      });
    }
    setErrors({});
  }, [editingTask, isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title?.trim()) newErrors.title = 'Task identity required';
    if (!formData.assigneeId) newErrors.assigneeId = 'Responsible staff must be assigned';
    if (!formData.dueDate) newErrors.dueDate = 'Deadline is mandatory';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden border border-slate-100 animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
        <header className="p-8 bg-indigo-600 text-white flex justify-between items-center shrink-0 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 blur-3xl -mr-16 -mt-16 rounded-full"></div>
          <div className="relative z-10">
            <h3 className="text-xl font-black uppercase tracking-tight">{editingTask ? 'Modify Task' : 'New Task'}</h3>
            <p className="text-indigo-100 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Resource Allocation Registry</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors relative z-10">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 space-y-6 overflow-y-auto custom-scrollbar flex-grow bg-slate-50/30">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
              <CheckCircle2 size={12} className="text-indigo-500" /> Task Name
            </label>
            <input 
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className={`w-full px-6 py-4 bg-white border rounded-2xl text-sm font-bold outline-none transition-all focus:ring-4 focus:ring-indigo-500/10 ${
                errors.title ? 'border-rose-300 focus:border-rose-500 shadow-rose-100' : 'border-slate-200 focus:border-indigo-500 shadow-sm'
              }`}
              placeholder="e.g. Inspect Dar-es-Salaam Port Discharges"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
              <AlignLeft size={12} className="text-slate-300" /> Description
            </label>
            <textarea 
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              placeholder="Provide context or specific site requirements..."
              className="w-full px-6 py-4 bg-white border border-slate-200 rounded-2xl text-sm outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all h-32 resize-none leading-relaxed shadow-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
              <User size={12} className="text-indigo-400" /> Responsible Staff
            </label>
            <div className="relative">
              <select 
                value={formData.assigneeId}
                onChange={e => setFormData({...formData, assigneeId: e.target.value})}
                className={`w-full px-6 py-4 bg-white border rounded-2xl text-sm font-bold outline-none transition-all appearance-none cursor-pointer pr-12 shadow-sm ${
                    errors.assigneeId ? 'border-rose-300' : 'border-slate-200'
                }`}
              >
                <option value="">-- Select Personnel --</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.fullName} ({user.role} - {user.designation || 'Specialist'})
                  </option>
                ))}
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                <ChevronDown size={18} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 pt-2">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                <Flag size={12} className="text-amber-500" /> Priority
              </label>
              <select 
                value={formData.priority}
                onChange={e => setFormData({...formData, priority: e.target.value as any})}
                className="w-full px-6 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:border-indigo-500 transition-all shadow-sm"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                <Calendar size={12} className="text-indigo-400" /> Deadline
              </label>
              <input 
                type="date"
                value={formData.dueDate}
                onChange={e => setFormData({...formData, dueDate: e.target.value})}
                className={`w-full px-6 py-4 bg-white border rounded-2xl text-sm font-bold outline-none focus:border-indigo-500 transition-all shadow-sm ${
                    errors.dueDate ? 'border-rose-300' : 'border-slate-200'
                }`}
              />
            </div>
          </div>
        </div>

        <footer className="p-8 bg-slate-50 border-t border-slate-100 flex justify-end gap-4 shrink-0">
          <button 
            onClick={onClose}
            className="px-8 py-3 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 rounded-2xl transition-all"
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            className="px-12 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2"
          >
            {editingTask ? 'Update Task' : 'Assign Task'}
          </button>
        </footer>
      </div>
    </div>
  );
};